/*!
 * Intersection Observer Performance Manager
 * Centralized, optimized intersection observer for animations and lazy loading
 * Version: 1.0.0 - Stickerine Theme Optimization
 */

window.StickerineIntersectionManager = {
  
  observers: new Map(),
  
  /**
   * ANIMATION OBSERVER: Handles scroll-triggered animations
   */
  animationObserver: null,
  
  /**
   * IMAGE OBSERVER: Handles lazy loading of images
   */
  imageObserver: null,
  
  /**
   * CAROUSEL OBSERVER: Handles lazy initialization of carousels
   */
  carouselObserver: null,

  /**
   * Initialize all observers with optimized settings
   */
  initialize() {
    this.createAnimationObserver();
    this.createImageObserver();
    this.createCarouselObserver();
    this.observeExistingElements();
  },

  /**
   * Animation Observer - For scroll-in animations
   */
  createAnimationObserver() {
    const options = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    this.animationObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          
          // Add animation classes
          if (element.classList.contains('fade-in')) {
            element.classList.add('visible');
          }
          
          if (element.classList.contains('slide-up')) {
            element.classList.add('visible');
          }
          
          if (element.classList.contains('observe-enter')) {
            element.classList.add('entered');
          }
          
          // Custom animations
          this.triggerCustomAnimation(element);
          
          // Unobserve after animation
          this.animationObserver.unobserve(element);
        }
      });
    }, options);
  },

  /**
   * Image Observer - For lazy loading
   */
  createImageObserver() {
    const options = {
      threshold: 0,
      rootMargin: '50px 0px'
    };

    this.imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          
          // Load image
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          
          // Load background image
          if (img.dataset.bgSrc) {
            img.style.backgroundImage = `url(${img.dataset.bgSrc})`;
            img.removeAttribute('data-bg-src');
          }
          
          // Add loaded class
          img.classList.add('loaded');
          
          this.imageObserver.unobserve(img);
        }
      });
    }, options);
  },

  /**
   * Carousel Observer - For lazy carousel initialization
   */
  createCarouselObserver() {
    const options = {
      threshold: 0.1,
      rootMargin: '100px 0px'
    };

    this.carouselObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const carousel = entry.target;
          this.initializeCarousel(carousel);
          this.carouselObserver.unobserve(carousel);
        }
      });
    }, options);
  },

  /**
   * Observe elements that already exist on page
   */
  observeExistingElements() {
    // Animation elements
    const animationElements = document.querySelectorAll('.fade-in, .slide-up, .observe-enter, [data-animate]');
    animationElements.forEach(el => this.animationObserver.observe(el));

    // Lazy load images
    const lazyImages = document.querySelectorAll('img[data-src], [data-bg-src]');
    lazyImages.forEach(img => this.imageObserver.observe(img));

    // Lazy carousels
    const carousels = document.querySelectorAll('[data-lazy-carousel]');
    carousels.forEach(carousel => this.carouselObserver.observe(carousel));
  },

  /**
   * Add new element to appropriate observer
   */
  observeElement(element, type = 'animation') {
    switch (type) {
      case 'animation':
        this.animationObserver.observe(element);
        break;
      case 'image':
        this.imageObserver.observe(element);
        break;
      case 'carousel':
        this.carouselObserver.observe(element);
        break;
    }
  },

  /**
   * Handle custom animations based on data attributes
   */
  triggerCustomAnimation(element) {
    const animationType = element.dataset.animate;
    const delay = parseInt(element.dataset.delay) || 0;
    
    setTimeout(() => {
      switch (animationType) {
        case 'slideLeft':
          element.style.transform = 'translateX(0)';
          element.style.opacity = '1';
          break;
        case 'slideRight':
          element.style.transform = 'translateX(0)';
          element.style.opacity = '1';
          break;
        case 'scale':
          element.style.transform = 'scale(1)';
          element.style.opacity = '1';
          break;
        case 'rotate':
          element.style.transform = 'rotate(0deg)';
          element.style.opacity = '1';
          break;
      }
    }, delay);
  },

  /**
   * Initialize carousel when it becomes visible
   */
  initializeCarousel(carousel) {
    const type = carousel.dataset.carouselType;
    
    // Add loading class
    carousel.classList.add('carousel-loading');
    
    switch (type) {
      case 'deals':
        this.initializeDealsCarousel(carousel);
        break;
      case 'testimonials':
        this.initializeTestimonialsCarousel(carousel);
        break;
      case 'shop-collection':
        this.initializeShopCollectionCarousel(carousel);
        break;
      default:
        this.initializeGenericCarousel(carousel);
    }
    
    // Remove loading class
    carousel.classList.remove('carousel-loading');
    carousel.classList.add('carousel-loaded');
  },

  /**
   * Initialize deals carousel with mobile optimization
   */
  initializeDealsCarousel(carousel) {
    const swiper = carousel.querySelector('.swiper');
    if (swiper && typeof Swiper !== 'undefined') {
      new Swiper(swiper, {
        slidesPerView: 'auto',
        spaceBetween: 20,
        speed: 400,
        centerInsufficientSlides: true,
        breakpoints: {
          320: {
            slidesPerView: 1.2,
            spaceBetween: 16,
          },
          768: {
            slidesPerView: 'auto',
            spaceBetween: 20,
          }
        }
      });
    }
  },

  /**
   * Initialize testimonials carousel
   */
  initializeTestimonialsCarousel(carousel) {
    const swiper = carousel.querySelector('.swiper');
    if (swiper && typeof Swiper !== 'undefined') {
      new Swiper(swiper, {
        slidesPerView: 'auto',
        spaceBetween: 24,
        speed: 600,
        freeMode: true,
        mousewheel: {
          forceToAxis: true,
          releaseOnEdges: true,
        },
        scrollbar: {
          el: '.swiper-scrollbar',
          hide: false,
          draggable: true,
        }
      });
    }
  },

  /**
   * Initialize shop collection carousel
   */
  initializeShopCollectionCarousel(carousel) {
    const swiper = carousel.querySelector('.swiper');
    if (swiper && typeof Swiper !== 'undefined') {
      new Swiper(swiper, {
        slidesPerView: 'auto',
        spaceBetween: 24,
        speed: 500,
        centeredSlides: false,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        scrollbar: {
          el: '.swiper-scrollbar',
          draggable: true,
        }
      });
    }
  },

  /**
   * Initialize generic carousel
   */
  initializeGenericCarousel(carousel) {
    const swiper = carousel.querySelector('.swiper');
    if (swiper && typeof Swiper !== 'undefined') {
      new Swiper(swiper, {
        slidesPerView: 'auto',
        spaceBetween: 20,
        speed: 400,
        lazy: true,
        preloadImages: false,
        watchSlidesProgress: true,
      });
    }
  },

  /**
   * Cleanup observers when not needed
   */
  cleanup() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    
    if (this.animationObserver) this.animationObserver.disconnect();
    if (this.imageObserver) this.imageObserver.disconnect();
    if (this.carouselObserver) this.carouselObserver.disconnect();
  },

  /**
   * Performance monitoring
   */
  getPerformanceMetrics() {
    return {
      observedElements: {
        animation: this.animationObserver ? this.animationObserver.takeRecords().length : 0,
        images: this.imageObserver ? this.imageObserver.takeRecords().length : 0,
        carousels: this.carouselObserver ? this.carouselObserver.takeRecords().length : 0
      },
      memoryUsage: performance.memory ? performance.memory.usedJSHeapSize : 'unavailable'
    };
  }
};

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.StickerineIntersectionManager.initialize();
  });
} else {
  window.StickerineIntersectionManager.initialize();
}